import { FC } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import {
  useApiErrorState,
  useSetApiErrorState,
} from '../../../contexts/ApiErrorContext';

import {
  ClientNotificationActionCreators,
  clientNotificationSelector,
} from '../../../redux/ui/clientNotification';
import { ErrorActionCreators, errorSelector } from '../../../redux/ui/error';
import { parseNumber } from '../../../utils/parseNumber';
import { MaintenanceWindowPage } from '../../pages/MaintenanceWindowPage/MaintenanceWindowPage';
import { ClientNotificationDialog } from '../ClientNotificationCatcher/ClientNotificationDialog';
import { ErrorDialog } from './ErrorDialog';

/**
 * グローバルなダイアログ表示を管理するコンポーネント
 * - APIエラー
 * - Sagaで発生するエラー
 * - クライアント側の情報メッセージ（エラーではない）
 */
export const ErrorCatcher: FC = () => {
  const dispatch = useDispatch();

  /**
   * APIで発生するすべてのエラー
   */
  const apiError = useApiErrorState();
  const setApiError = useSetApiErrorState();

  /**
   * Sagaで発生する予期せぬエラー
   */
  const errorState = useSelector(errorSelector, shallowEqual);

  /**
   * クライアント側の情報メッセージ（エラーではない）
   */
  const clientNotificationState = useSelector(
    clientNotificationSelector,
    shallowEqual
  );

  if (apiError.isError) {
    // 503: メンテナンス
    if (apiError.data.maintenance != null) {
      return <MaintenanceWindowPage maintenance={apiError.data.maintenance} />;
    }

    const statusCode = parseNumber(apiError.data.statusCode);
    // 5xx: サーバーエラー
    if (statusCode >= 500) {
      throw new Error(apiError.data.message);
    }

    // 422: 商圏店舗が存在しない場合はErrorDialogでメッセージを表示（店舗切り替えを可能にするためOutletも表示）
    if (
      statusCode === 422 &&
      apiError.data.message.includes('商圏店舗が存在しません')
    ) {
      return (
        <>
          <Outlet />
          <ErrorDialog
            statusCode={422}
            isOpen={apiError.isError}
            close={() => setApiError({ isError: false })}
            message={apiError.data.message}
          />
        </>
      );
    }

    // 422: POST/PUT/PATCH時に発生したエラーは必ずクライエント側で制御
    // 422: 422エラーの場合ErrorDialogを表示させたくない為、Outletを返す
    if (statusCode === 422) {
      return <Outlet />;
    }

    // 429: リクエスト制限エラー
    // 主にユーザー編集APIでのIP制限リミットによるエラーコードとして使われている
    if (statusCode === 429) {
      throw new Error(apiError.data.message);
    }

    // 4xx: クライアントエラー
    return (
      <>
        <Outlet />
        <ErrorDialog
          statusCode={!isNaN(statusCode) ? statusCode : undefined}
          isOpen={apiError.isError}
          close={() => setApiError({ isError: false })}
          message={apiError.data.message}
        />
      </>
    );
  }

  if (errorState.error.isError) {
    return (
      <>
        <Outlet />
        <ErrorDialog
          isOpen={errorState.error.isError}
          close={() => dispatch(ErrorActionCreators.clearError())}
          message={errorState.error.message}
        />
      </>
    );
  }

  if (clientNotificationState.clientNotification.isOpen) {
    return (
      <>
        <Outlet />
        <ClientNotificationDialog
          isOpen={clientNotificationState.clientNotification.isOpen}
          close={() =>
            dispatch(ClientNotificationActionCreators.clearClientNotification())
          }
          title={clientNotificationState.clientNotification.title}
          message={clientNotificationState.clientNotification.message}
        />
      </>
    );
  }

  return <Outlet />;
};
